import {
  ACTIVATION_GROUP_LABEL,
  type ExportModel,
  type ExportModelColumn,
} from './build-export-model.js';
import { estimateWrappedLineCount } from './estimate-row-heights.js';
import {
  CSS_PX_PER_PT,
  DENSITY_PADDING_PX,
  LINE_HEIGHT_FACTOR,
  type PageGeometry,
  ROW_LABEL_WIDTH_PX,
  SUBJECT_DESCRIPTION_RESERVED_PX,
} from './page-geometry.js';
import type { BulletLine } from './parse-bullet-lines.js';

/**
 * The six activation levier rows share no single underlying `model.rows` key
 * once merged into one section (spec R2/P2 Dossier), so this section's rows
 * carry a synthetic key instead of a real one. It cannot collide with a real
 * row key by exact-string equality: dimension keys are UUIDs (36 characters,
 * five hyphen-separated groups) and activation keys are the bare levier order
 * ('1'-'6') — neither is ever the literal string `'activation-group'`, even
 * though UUIDs do themselves contain hyphens.
 *
 * Consequence: content-anchored highlight annotations against activation
 * cells (keyed by the *real* levier row key) do not round-trip through the
 * transposed structure — a known, documented limitation, not a bug. Dimension
 * sections are unaffected: their `rowKey` is the real dimension row key.
 */
const ACTIVATION_GROUP_ROW_KEY = 'activation-group';

export interface TransposedSectionRow {
  palier: ExportModelColumn;
  lines: BulletLine[];
  rowKey: string;
}

export interface TransposedSection {
  /** Section title — the dimension title, or ACTIVATION_GROUP_LABEL for the activation block. */
  title: string;
  /** One row per selected palier: its header cell + this section's cell lines. */
  rows: TransposedSectionRow[];
  /** Estimated unscaled height: title bar + Σ row heights (same Task 3 heuristic, content width = pageContentWidth − palier label column). */
  heightPx: number;
}

/**
 * Builds one section per dimension row (title, in model order) plus one
 * leading section grouping every activation row under `ACTIVATION_GROUP_LABEL`
 * — mirroring how the non-transposed grid spans the six activation rows under
 * one row-label cell (`ACTIVATION_GROUP_LABEL` in `render-export-html.ts`).
 *
 * Each section has exactly one row per selected palier ("paliers as rows,
 * one dimension per block" — spec R2/P2). For the activation section, a
 * palier's row concatenates that palier's content across every activation
 * row, in levier order (see `ACTIVATION_GROUP_ROW_KEY`).
 */
export function buildTransposedSections(input: {
  model: ExportModel;
  fontSizePt: number;
  density: 'compact' | 'normal' | 'aere';
  contentWidthPx: number;
}): TransposedSection[] {
  const { model, fontSizePt, density, contentWidthPx } = input;
  const fontPx = fontSizePt * CSS_PX_PER_PT;
  const linePx = Math.ceil(fontPx * LINE_HEIGHT_FACTOR);
  const paddingPx = DENSITY_PADDING_PX[density];
  // The palier label column sits beside the content cell in every row, so the
  // content itself only gets the remainder of the section table's width.
  const rowContentWidthPx = contentWidthPx - ROW_LABEL_WIDTH_PX;

  /** Task 3's char-wrapping heuristic (`estimateWrappedLineCount`), summed
   *  across every line in a cell and turned into a pixel height — the same
   *  `maxLines * linePx + 2 * padding` shape `estimateRowHeights` uses, but
   *  summed rather than maxed because a transposed row has a single content
   *  cell, not one per column. */
  function cellHeightPx(lines: readonly BulletLine[], widthPx: number): number {
    let count = 0;
    for (const line of lines) {
      count += estimateWrappedLineCount(line.text, line.hasBullet, widthPx, paddingPx, fontPx);
    }
    return Math.max(1, count) * linePx + 2 * paddingPx;
  }

  function buildSection(
    title: string,
    linesForPalier: (palierNumber: number) => BulletLine[],
    rowKey: string,
  ): TransposedSection {
    const rows: TransposedSectionRow[] = model.columns.map((palier) => ({
      palier,
      lines: linesForPalier(palier.number),
      rowKey,
    }));
    const titleHeightPx = cellHeightPx([{ text: title, hasBullet: false }], contentWidthPx);
    const rowsHeightPx = rows.reduce(
      (sum, row) => sum + cellHeightPx(row.lines, rowContentWidthPx),
      0,
    );
    return { title, rows, heightPx: titleHeightPx + rowsHeightPx };
  }

  const sections: TransposedSection[] = [];

  const activationRows = model.rows.filter((row) => row.kind === 'activation');
  if (activationRows.length > 0) {
    sections.push(
      buildSection(
        ACTIVATION_GROUP_LABEL,
        (palierNumber) =>
          activationRows.flatMap((row) => model.cellContent[row.key]?.[palierNumber] ?? []),
        ACTIVATION_GROUP_ROW_KEY,
      ),
    );
  }

  for (const row of model.rows) {
    if (row.kind !== 'dimension') continue;
    sections.push(
      buildSection(
        row.label,
        (palierNumber) => model.cellContent[row.key]?.[palierNumber] ?? [],
        row.key,
      ),
    );
  }

  return sections;
}

/**
 * The vertical budget one transposed page gives its sections — the one
 * derivation both the renderer and `planPdfDocument` feed
 * `computeTransposedPages`, so the page count the viewer plans is the page
 * count Chromium prints.
 *
 * Conservative, uniform-per-page — the same trade-off `computeTilePages`'
 * `reservedHeaderHeightPx` makes: every page reserves the *first*-page header
 * height even though only page 1 renders one, so the packing never overflows.
 * The subject/description block is reserved the same way (it renders inside
 * the fixed `overflow: hidden` page box, at its pinned
 * `SUBJECT_DESCRIPTION_RESERVED_PX` height), and so is the footer, which sits
 * at the bottom of every page (`reservedFooterHeightPx` = `FOOTER_RESERVED_PX`
 * when `documentFooterEnabled`, else 0).
 */
export function transposedUsableHeightPx(input: {
  geometry: PageGeometry;
  documentHeaderHeightPx: number;
  subjectDescriptionIncluded: boolean;
  reservedFooterHeightPx: number;
}): number {
  return (
    input.geometry.pageHeightPx -
    2 * input.geometry.marginPx -
    input.documentHeaderHeightPx -
    (input.subjectDescriptionIncluded ? SUBJECT_DESCRIPTION_RESERVED_PX : 0) -
    input.reservedFooterHeightPx
  );
}

/**
 * Packs sections vertically into pages (greedy, a section splits across pages
 * only when taller than a full page); returns one TilePage-compatible page
 * list where columnPageIndex is always 0.
 *
 * Splitting a single section across two pages is out of scope (brief step 3):
 * a section taller than `usableHeightPx` alone is placed on its own page and
 * left to overflow — clipped by the fixed page box, same as `packIntoPages`
 * in `compute-tile-pages.ts` does for an oversized single row/column.
 */
export function computeTransposedPages(
  sections: readonly TransposedSection[],
  usableHeightPx: number,
): { sectionIndices: number[] }[] {
  const pages: { sectionIndices: number[] }[] = [];
  let current: number[] = [];
  let currentHeightPx = 0;
  sections.forEach((section, index) => {
    if (current.length > 0 && currentHeightPx + section.heightPx > usableHeightPx) {
      pages.push({ sectionIndices: current });
      current = [];
      currentHeightPx = 0;
    }
    current.push(index);
    currentHeightPx += section.heightPx;
  });
  if (current.length > 0) pages.push({ sectionIndices: current });
  return pages;
}
